import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flag, Search, X, ExternalLink, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Pagination, { paginate } from '../../components/Pagination';
import ClampedText from '../../components/ClampedText';
import API from '../../utils/api';
import { onAdminRefresh } from '../../utils/adminSocket';
import '../IncompleteRegistrations/IncompleteRegistrations.css';
import '../DeactivatedAccounts/DeactivatedAccounts.css';
import './ProfileReports.css';

const PAGE_SIZE = 10;
const STATUSES = [['open', 'Open'], ['reviewing', 'Reviewing'], ['resolved', 'Resolved'], ['dismissed', 'Dismissed']];
const labelOf = (status) => (STATUSES.find(([value]) => value === status) || [status, status])[1];
const nameOf = (person) => (person && (person.profileName || person.name)) || '—';

// What members reported about a profile: the profile ID, the reason and their explanation. The admin reviews each one.
const ProfileReports = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [reports, setReports] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('all');
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(null); // the report whose details are open
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const response = await API.get('/admin/profile-reports');
            setReports(response.data.reports || []);
            setCounts(response.data.counts || {});
        } catch (error) {
            if (error.response?.status !== 429) toast.error(error.response?.data?.message || 'Could not load the reports');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const off = onAdminRefresh(load);
        return () => off();
    }, [load]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return reports.filter(report => {
            if (status !== 'all' && report.status !== status) return false;
            if (!needle) return true;
            return [report.reportedMemberId, report.reportedName, report.reason, report.details, report.reporter?.name, report.reporter?.memberId, report.reporter?.mobile]
                .filter(Boolean).some(value => String(value).toLowerCase().includes(needle));
        });
    }, [reports, status, query]);

    useEffect(() => {
        const last = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
        if (page > last) setPage(last);
    }, [visible.length, page]);

    const total = reports.length;

    const openReport = (report) => {
        setOpen(report);
        setNote(report.adminNote || '');
        if (!report.adminSeen) {
            // opening a new report marks it as seen, and the sidebar badge goes down
            setReports(current => current.map(item => (item._id === report._id ? { ...item, adminSeen: true } : item)));
            API.put(`/admin/profile-reports/${report._id}`, {}).then(() => window.dispatchEvent(new Event('reportsSeen'))).catch(() => { });
        }
    };

    const save = async (nextStatus) => {
        if (!open) return;
        setSaving(true);
        try {
            const response = await API.put(`/admin/profile-reports/${open._id}`, { status: nextStatus || open.status, adminNote: note });
            setReports(current => current.map(item => (item._id === open._id ? { ...item, ...response.data.report } : item)));
            toast.success('Report updated');
            setOpen(null);
            load();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not update the report');
        } finally { setSaving(false); }
    };

    return (
        <div className="incomplete-layout">
            <Sidebar />
            <main className="admin-content incomplete-content">
                <Navbar title={<span className="incomplete-title"><Flag size={19} /> Profile Reports</span>} />
                <div className="incomplete-page">
                    <header className="incomplete-heading">
                        <div>
                            <h2>Profile reports</h2>
                            <p>Reports members sent about other profiles: the profile ID, the reason and what they wrote. Open one to read it in full and mark what was done.</p>
                        </div>
                        <span className="incomplete-total"><Flag size={16} /> {counts.open || 0} open{reports.some(r => !r.adminSeen && r.status === 'open') && <b className="report-new report-new--head"><i />{reports.filter(r => !r.adminSeen && r.status === 'open').length} new</b>}</span>
                    </header>

                    <section className="incomplete-panel">
                        <div className="incomplete-toolbar">
                            <label className="incomplete-search"><Search size={17} /><input type="search" value={query} onChange={e => { setQuery(e.target.value); setPage(1); }} placeholder="Search profile ID, name, reason or details..." /></label>
                            <div className="incomplete-tabs">
                                {[['all', 'All', total], ...STATUSES.map(([value, label]) => [value, label, counts[value] || 0])].map(([value, label, count]) => (
                                    <button type="button" key={value} className={status === value ? 'active' : ''} onClick={() => { setStatus(value); setPage(1); }}>{label}<span>{count}</span></button>
                                ))}
                            </div>
                        </div>

                        {loading ? <div className="incomplete-state">Loading reports...</div> : visible.length === 0 ? (
                            <div className="incomplete-state"><Flag size={36} /><strong>No reports</strong><span>Reports from members will appear here.</span></div>
                        ) : (
                            <>
                                <div className="deactivated-table-wrap">
                                    <table className="deactivated-table report-table">
                                        <thead>
                                            <tr><th>#</th><th>Reported profile</th><th>Times reported</th><th>Reported by</th><th>Reason</th><th>Explanation</th><th>Date</th><th>Status</th></tr>
                                        </thead>
                                        <tbody>
                                            {paginate(visible, page, PAGE_SIZE).map((report, index) => (
                                                <tr key={report._id} className={`report-row ${report.adminSeen ? '' : 'is-new'}`} onClick={() => openReport(report)}>
                                                    <td className="is-num">{String((page - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}</td>
                                                    <td><strong className="report-id">{report.reportedMemberId || 'No ID'}</strong>{!report.adminSeen && <span className="report-new" title="New: nobody has opened this report yet"><i />NEW</span>}<small className="report-sub">{report.reportedName || nameOf(report.reportedUser)}</small></td>
                                                    <td><span className={`report-count ${report.profileUniqueReporters >= 3 ? 'is-hot' : ''}`} title="All reports of this profile, and how many different members sent them">{report.profileTotalReports} {report.profileTotalReports === 1 ? 'report' : 'reports'}</span><small className="report-sub">{report.profileUniqueReporters} {report.profileUniqueReporters === 1 ? 'member' : 'members'}</small></td>
                                                    <td><span className="deactivated-cut">{nameOf(report.reporter)}</span><small className="report-sub">{report.reporter?.memberId || report.reporter?.mobile || ''}</small></td>
                                                    <td><span className="report-reason">{report.reason}</span></td>
                                                    <td><ClampedText text={report.details} lines={3} /></td>
                                                    <td className="deactivated-nowrap">{new Date(report.createdAt).toLocaleDateString('en-IN')}</td>
                                                    <td><span className={`report-status is-${report.status}`}>{labelOf(report.status)}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination page={page} total={visible.length} pageSize={PAGE_SIZE} onChange={setPage} />
                            </>
                        )}
                    </section>
                </div>
            </main>

            {open && (
                <div className="report-modal" role="presentation" onClick={() => setOpen(null)}>
                    <div className="report-modal__card" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                        <header>
                            <h3><Flag size={17} /> Report on {open.reportedMemberId || 'a profile'}</h3>
                            <button type="button" onClick={() => setOpen(null)} aria-label="Close"><X size={18} /></button>
                        </header>
                        <dl>
                            <div><dt>Reported profile</dt><dd><strong>{open.reportedMemberId || 'No ID'}</strong> {open.reportedName && `· ${open.reportedName}`}
                                {open.profile && <button type="button" className="report-link" onClick={() => navigate(`/profiles/${open.profile}`)}><ExternalLink size={13} /> Open profile</button>}</dd></div>
                            <div><dt>Times reported</dt><dd><strong>{open.profileTotalReports}</strong> {open.profileTotalReports === 1 ? 'report' : 'reports'} from <strong>{open.profileUniqueReporters}</strong> different {open.profileUniqueReporters === 1 ? 'member' : 'members'}</dd></div>
                            <div><dt>Reported by</dt><dd>{nameOf(open.reporter)} {open.reporter?.memberId && `· ${open.reporter.memberId}`} {open.reporter?.mobile && `· +91 ${open.reporter.mobile}`}</dd></div>
                            <div><dt>Date</dt><dd>{new Date(open.createdAt).toLocaleString('en-IN')}</dd></div>
                            <div><dt>Reason</dt><dd><span className="report-reason">{open.reason}</span></dd></div>
                            <div><dt>Explanation</dt><dd className="report-details">{open.details}</dd></div>
                            {open.alsoBlocked && <div><dt>Also blocked</dt><dd>The reporter also blocked this profile.</dd></div>}
                        </dl>
                        <label className="report-modal__label" htmlFor="report-note">Admin note (only admins see this)</label>
                        <textarea id="report-note" rows={3} maxLength={1000} value={note} onChange={e => setNote(e.target.value)} placeholder="What was checked or done about this report" />
                        <footer>
                            <span className={`report-status is-${open.status}`}>{labelOf(open.status)}</span>
                            <div>
                                <button type="button" onClick={() => save('reviewing')} disabled={saving}>Mark reviewing</button>
                                <button type="button" onClick={() => save('dismissed')} disabled={saving}>Dismiss</button>
                                <button type="button" className="is-main" onClick={() => save('resolved')} disabled={saving}><Save size={14} /> Resolve</button>
                            </div>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileReports;
